'use client'
import * as React from 'react'
import { cva, VariantProps } from 'class-variance-authority'
import { useLocalAvatarVersion } from '../hooks/use-local-avatar-version'
import { clsx } from 'clsx'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { useUserData } from '~/hooks-data/use-user-data'
import { Invite } from '@basehub/data/replicache/index'
import { SYSTEM_AUTHORS } from '@basehub/shared-constants'
import { isPlaygroundUser } from '@basehub/data/replicache/playground/ids'

export const $avatar = cva('rounded-full select-none bg-dark-base', {
  variants: {
    size: {
      xsmall: 'size-4 min-w-4 text-xs',
      petite: 'size-5 min-w-5 text-xs',
      small: 'size-6 min-w-6 text-xs',
      medium: 'size-8 min-w-8 text-sm',
      large: 'size-20 min-w-20 text-sm',
      fill: 'size-full text-xs',
    },
    withBorder: { true: 'border border-dark-border-solid' },
  },
  defaultVariants: { size: 'small' },
})

type BaseHubAvatarComponentProps = {
  className?: string
  name: string
  id: string | undefined
  forceSrc?: string | null
  style?: React.CSSProperties
}

export type BaseHubAvatarProps = VariantProps<typeof $avatar> &
  BaseHubAvatarComponentProps

export const BaseHubAvatar = React.forwardRef<
  HTMLImageElement,
  BaseHubAvatarProps
>(({ size, className, name, id: _id, forceSrc, style, withBorder }, ref) => {
  const [localAvatarVersion] = useLocalAvatarVersion()
  const userIsPlayground = isPlaygroundUser(_id)
  const id = userIsPlayground ? SYSTEM_AUTHORS.UNKNOWN.id : _id

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      className={$avatar({
        size,
        withBorder,
        className: clsx(className, 'select-none object-cover'),
      })}
      style={style}
      src={
        forceSrc ??
        `/api/avatars/${id}` +
          (localAvatarVersion && localAvatarVersion.expiresAt >= Date.now()
            ? `?v=${localAvatarVersion.version}`
            : '')
      }
      alt={`Avatar of ${name}`}
      loading="eager"
    />
  )
})

type BaseHubAvatarWithBubbleIconProps = {
  bubbleClassName?: string
  className?: string
  avatarClassName?: string
  id: string | undefined
  name: string
  withBorder?: boolean
  size?: 'xsmall' | 'small' | 'medium' | 'fill'
  tooltip?: string
  bubbleIcon: React.ReactNode
}
export const AvatarWithBubbleIcon = React.forwardRef<
  HTMLImageElement,
  BaseHubAvatarWithBubbleIconProps
>(
  (
    {
      id,
      name,
      bubbleClassName,
      className,
      avatarClassName,
      withBorder = true,
      size = 'fill',
      tooltip,
      bubbleIcon,
    },
    ref
  ) => {
    const Wrapper = tooltip
      ? ({ children }: React.PropsWithChildren) => (
          <SimpleTooltip content={tooltip} disabled={!Boolean(tooltip)}>
            {children}
          </SimpleTooltip>
        )
      : React.Fragment

    return (
      <div
        ref={ref}
        className={clsx(
          className,
          'w-8 h-8 flex items-center justify-center relative rounded-full shrink-0'
        )}
      >
        <Wrapper>
          <span className="size-full">
            <BaseHubAvatar
              className={avatarClassName}
              id={id}
              name={name}
              size={size}
              withBorder={withBorder}
            />
          </span>
        </Wrapper>
        <div
          className={clsx(
            bubbleClassName,
            'absolute w-6 h-6 border border-dark-base rounded-full left-1/2 -translate-x-1/2 top-6 flex items-center justify-center'
          )}
        >
          {bubbleIcon}
        </div>
      </div>
    )
  }
)

export const InvitedUserAvatar = ({
  size = 'medium',
  invite,
  className,
}: {
  invite: Invite
  size?: BaseHubAvatarProps['size']
  className?: string
}) => {
  const { data: user } = useUserData(invite.userIdLock ?? undefined)

  if (user) {
    return (
      <BaseHubAvatar
        id={user.id}
        name={user.username ?? ''}
        size={size}
        className={className}
        forceSrc={user?.isMe ? (user.imageUrl ?? undefined) : undefined}
      />
    )
  }

  return (
    <div
      className={clsx(
        {
          'size-4 text-[12px]': size === 'xsmall',
          'size-6 text-xs': size === 'small',
          'size-8 text-xs': size === 'medium',
          'size-10 text-base': size === 'fill',
        },
        'flex items-center justify-center border rounded-full text-dark-faint border-dark-border-solid',
        className
      )}
    >
      {invite.emailLock?.substring(0, 1).toUpperCase()}
    </div>
  )
}
